/**
 * Shared URL and link validation for Glimm transitions.
 */

import { resolvePalette } from 'glimm';

export function isDebugEnabled(config = window.glimmTransitions) {
  const value = config?.debug;
  return value === true || value === 1 || value === '1';
}

export function debugLog(config, ...args) {
  if (isDebugEnabled(config)) {
    console.log('[glimm-transitions]', ...args);
  }
}

export function debugWarn(config, ...args) {
  if (isDebugEnabled(config)) {
    console.warn('[glimm-transitions]', ...args);
  }
}

/** Log a transition phase with a high-resolution timestamp (debug only). */
export function debugPhase(config, phase, data = {}) {
  if (!isDebugEnabled(config)) return;
  console.log('[glimm-transitions]', `[${phase}]`, {
    t: Math.round(performance.now()),
    ...data,
  });
}

export function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function normalizePath(pathname) {
  if (!pathname) return '/';
  return pathname.endsWith('/') && pathname.length > 1
    ? pathname.slice(0, -1)
    : pathname;
}

export function isSamePage(href, currentHref = window.location.href) {
  try {
    const next = new URL(normalizeHref(href, currentHref), currentHref);
    const current = new URL(currentHref);
    return (
      next.hostname === current.hostname &&
      normalizePath(next.pathname) === normalizePath(current.pathname) &&
      next.search === current.search
    );
  } catch {
    return false;
  }
}

export function isExcludedPath(href, excludePaths, baseUrl = window.location.origin) {
  try {
    const url = new URL(href, baseUrl);
    const path = url.pathname || '/';
    return excludePaths.some((prefix) => {
      if (!prefix) return false;
      const normalized = prefix.startsWith('/') ? prefix : `/${prefix}`;
      return path === normalized || path.startsWith(`${normalized}/`);
    });
  } catch {
    return true;
  }
}

export function isInternalLink(anchor, baseOrigin = window.location.origin) {
  if (!anchor?.href) return false;
  try {
    const url = new URL(anchor.href, baseOrigin);
    const base = new URL(baseOrigin);
    return url.hostname === base.hostname;
  } catch {
    return false;
  }
}

/** Normalize href to current page protocol when host matches. */
export function normalizeHref(href, baseOrigin = window.location.origin) {
  try {
    const url = new URL(href, baseOrigin);
    const base = new URL(baseOrigin);
    if (url.hostname === base.hostname && url.protocol !== base.protocol) {
      url.protocol = base.protocol;
    }
    return url.href;
  } catch {
    return href;
  }
}

export function shouldSkipLink(anchor, config) {
  if (!anchor || anchor.hasAttribute('data-glimm-skip')) return true;
  if (anchor.target && anchor.target !== '_self') return true;
  if (anchor.hasAttribute('download')) return true;

  const href = anchor.getAttribute('href') || '';
  if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
    return true;
  }

  if (!isInternalLink(anchor)) return true;
  if (isSamePage(normalizeHref(anchor.href))) return true;
  if (isExcludedPath(normalizeHref(anchor.href), config.excludePaths || [])) return true;

  if (href.includes('/wp-admin') || href.includes('wp-login.php')) return true;

  return false;
}

export function matchesLinkScope(anchor, config) {
  const selectors = config.linkSelectors || [];
  if (!selectors.length || config.linkScope === 'off') return false;

  if (config.linkScope === 'opt_in') {
    return Boolean(anchor.closest('[data-glimm], .glimm-transition'));
  }

  return selectors.some((selector) => {
    try {
      return anchor.matches(selector);
    } catch {
      return false;
    }
  });
}

export function readAestheticsFromForm() {
  const get = (id) => document.getElementById(id);
  return {
    palette: get('glimm_palette')?.value || 'prism',
    direction: get('glimm_direction')?.value || 'ltr',
    easing: get('glimm_easing')?.value || 'easeInOutCubic',
    sweepMs: Number(get('glimm_sweep_ms')?.value || 800),
    outroMs: Number(get('glimm_outro_ms')?.value || 350),
    midpoint: Number(get('glimm_midpoint')?.value || 0.5),
    bandTight: Number(get('glimm_band_tight')?.value || 14),
    brightness: Number(get('glimm_brightness')?.value || 1),
    waveAmount: Number(get('glimm_wave_amount')?.value || 1),
    rippleAmount: Number(get('glimm_ripple_amount')?.value || 1),
    waveSpeed: Number(get('glimm_wave_speed')?.value || 1),
  };
}

export function aestheticsToSweepOptions(aesthetics = {}) {
  return {
    ...shaderOptionsFromAesthetics(aesthetics),
    easing: aesthetics.easing,
    sweepMs: aesthetics.sweepMs,
    outroMs: aesthetics.outroMs,
    midpoint: aesthetics.midpoint,
  };
}

/** Resolved glimm shader options (palette object, never a string name). */
export function shaderOptionsFromAesthetics(aesthetics = {}) {
  return {
    palette: resolvePalette(aesthetics.palette),
    bandTight: aesthetics.bandTight,
    direction: aesthetics.direction,
    waveAmount: aesthetics.waveAmount,
    rippleAmount: aesthetics.rippleAmount,
    waveSpeed: aesthetics.waveSpeed,
    brightness: aesthetics.brightness,
  };
}

export const PENDING_KEY = 'glimm:pending';

export function savePendingHandoff(data) {
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(data));
}

export function readPendingHandoff() {
  const raw = sessionStorage.getItem(PENDING_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearPendingHandoff() {
  sessionStorage.removeItem(PENDING_KEY);
}

const HEADER_SELECTOR = 'header, footer, .site-header, .site-footer';

export function getSwapSelector() {
  return window.glimmTransitions?.swapSelector || 'main';
}

/** CSS selectors for midpoint landing opacity (configured swap + block-theme fallback). */
export function getContentFadeLandingSelectors(swapSelector = getSwapSelector()) {
  const selectors = new Set([
    swapSelector,
    '.wp-site-blocks > :not(header):not(footer)',
  ]);
  return [...selectors].join(',');
}

/**
 * Find the swappable content region in a document.
 * Prefers <main>, then non-header/footer children of .wp-site-blocks.
 */
export function findContentRegion(doc = document) {
  const configured = doc.querySelector?.bind(doc);
  if (!configured) return null;

  const swapSelector = getSwapSelector();

  const configuredEl = configured(swapSelector);
  if (configuredEl) {
    return { type: 'single', element: configuredEl };
  }

  const main = configured('main');
  if (main) {
    return { type: 'single', element: main };
  }

  const siteBlocks = configured('.wp-site-blocks');
  if (!siteBlocks) {
    return null;
  }

  const contentNodes = [...siteBlocks.children].filter(
    (child) => !child.matches(HEADER_SELECTOR)
  );

  if (!contentNodes.length) {
    return null;
  }

  return { type: 'multi', parent: siteBlocks, nodes: contentNodes };
}

/** Head assets that never change between pages — skip PJAX sync. */
const STATIC_HEAD_STYLE_IDS = new Set([
  'wp-emoji-styles-inline-css',
  'wp-block-library-inline-css',
  'wp-img-auto-sizes-contain-inline-css',
  'wp-block-template-skip-link-inline-css',
  'sccss',
]);

/** Block stylesheets that must stay in the document (header nav). */
const PERSISTENT_STYLESHEET_IDS = new Set([
  'wp-block-navigation-css',
]);

/** Whether a head <style> tag should be synced from the fetched document. */
export function isPageSpecificStyleId(id) {
  if (!id) return false;
  return !STATIC_HEAD_STYLE_IDS.has(id);
}

function collectPageStyles(doc) {
  const styles = {};
  doc.head?.querySelectorAll('style[id]').forEach((el) => {
    if (isPageSpecificStyleId(el.id)) {
      styles[el.id] = el.textContent;
    }
  });
  return styles;
}

function collectStylesheetLinks(doc) {
  const links = {};
  doc.head?.querySelectorAll('link[rel="stylesheet"][id]').forEach((el) => {
    links[el.id] = el.getAttribute('href');
  });
  return links;
}

/** Snapshot inline layout styles and body class for PJAX rollback. */
export function snapshotPageStyles() {
  return {
    styles: collectPageStyles(document),
    bodyClass: document.body.className,
  };
}

/** Restore a snapshot from {@link snapshotPageStyles}. */
export function restorePageStyles(snapshot) {
  if (!snapshot) return;

  Object.entries(snapshot.styles).forEach(([id, text]) => {
    const el = document.getElementById(id);
    if (el && text != null) {
      el.textContent = text;
    }
  });

  document.body.className = snapshot.bodyClass;
}

function syncInlineStyles(fromDoc) {
  const nextStyles = collectPageStyles(fromDoc);
  const currentIds = new Set();
  document.head.querySelectorAll('style[id]').forEach((el) => {
    if (isPageSpecificStyleId(el.id)) {
      currentIds.add(el.id);
    }
  });

  Object.entries(nextStyles).forEach(([id, text]) => {
    const current = document.getElementById(id);
    if (current) {
      current.textContent = text;
    } else {
      const next = fromDoc.getElementById(id);
      if (next) {
        document.head.appendChild(document.importNode(next, true));
      }
    }
  });

  // Remove stale page styles entirely (empty tags can interfere with cascade).
  currentIds.forEach((id) => {
    if (!(id in nextStyles)) {
      document.getElementById(id)?.remove();
    }
  });
}

function syncStylesheetLinks(fromDoc) {
  const nextLinks = collectStylesheetLinks(fromDoc);
  const currentIds = new Set();
  document.head.querySelectorAll('link[rel="stylesheet"][id]').forEach((el) => {
    currentIds.add(el.id);
  });

  Object.entries(nextLinks).forEach(([id, href]) => {
    if (!href) return;
    const current = document.getElementById(id);
    if (current) {
      if (current.getAttribute('href') !== href) {
        current.setAttribute('href', href);
      }
      return;
    }
    const next = fromDoc.getElementById(id);
    if (next) {
      document.head.appendChild(document.importNode(next, true));
    }
  });

  currentIds.forEach((id) => {
    if (PERSISTENT_STYLESHEET_IDS.has(id)) return;
    if (!(id in nextLinks) && id.startsWith('wp-block-')) {
      document.getElementById(id)?.remove();
    }
  });

  // Home omits navigation inline CSS and relies on the linked stylesheet.
  if (!document.getElementById('wp-block-navigation-css')) {
    const navSheet = fromDoc.getElementById('wp-block-navigation-css');
    if (navSheet) {
      document.head.appendChild(document.importNode(navSheet, true));
    }
  }
}

/**
 * Sync page-specific head assets from a fetched document.
 * Updates inline styles, block stylesheet links, and body classes.
 */
export function syncPageStyles(fromDoc) {
  if (!fromDoc?.head) return;

  syncInlineStyles(fromDoc);
  syncStylesheetLinks(fromDoc);

  if (fromDoc.body?.className != null) {
    document.body.className = fromDoc.body.className;
  }
}

export function removeMidpointLandingStyle() {
  document.getElementById('glimm-midpoint-landing')?.remove();
}

/** Whether two content regions can be swapped without restructuring the page. */
export function areContentRegionsCompatible(currentRegion, nextRegion) {
  if (!currentRegion || !nextRegion) return false;
  if (currentRegion.type !== nextRegion.type) return false;

  if (currentRegion.type === 'single') {
    return Boolean(currentRegion.element && nextRegion.element);
  }

  if (currentRegion.type === 'multi') {
    return (
      currentRegion.nodes?.length > 0 &&
      currentRegion.nodes.length === nextRegion.nodes.length
    );
  }

  return false;
}

export function swapContentRegions(currentRegion, nextRegion) {
  if (!currentRegion || !nextRegion) {
    throw new Error('PJAX: content region not found');
  }

  if (!areContentRegionsCompatible(currentRegion, nextRegion)) {
    throw new Error('PJAX: incompatible content regions');
  }

  const currentNodes = currentRegion.type === 'multi'
    ? currentRegion.nodes
    : [currentRegion.element];
  const nextNodes = nextRegion.type === 'multi'
    ? nextRegion.nodes
    : [nextRegion.element];

  const parent = currentRegion.type === 'multi'
    ? currentRegion.parent
    : currentRegion.element.parentElement;

  if (!parent || !currentNodes.length || !nextNodes.length) {
    throw new Error('PJAX: unable to map content regions');
  }

  const insertBefore = currentNodes[currentNodes.length - 1].nextSibling;
  currentNodes.forEach((node) => node.remove());
  nextNodes.forEach((node) => {
    parent.insertBefore(node, insertBefore);
  });
}

export function getRegionElements(region) {
  if (!region) return [];
  return region.type === 'multi' ? region.nodes : [region.element];
}

export function setRegionOpacity(region, opacity, transitionMs = null) {
  getRegionElements(region).forEach((el) => {
    el.classList.add('glimm-content-fade');
    if (transitionMs != null && transitionMs > 0) {
      el.style.transition = `opacity ${transitionMs}ms ease`;
    } else {
      el.style.transition = '';
    }
    el.style.opacity = String(opacity);
  });
}

export function animateRegionOpacity(region, from, to, durationMs) {
  const els = getRegionElements(region);
  if (!els.length) {
    return Promise.resolve();
  }

  if (durationMs <= 0) {
    setRegionOpacity(region, to, null);
    return Promise.resolve();
  }

  els.forEach((el) => {
    el.classList.add('glimm-content-fade');
    el.style.transition = 'none';
    el.style.opacity = String(from);
  });

  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      els.forEach((el) => {
        el.style.transition = `opacity ${durationMs}ms ease`;
        el.style.opacity = String(to);
      });
      setTimeout(resolve, durationMs);
    });
  });
}

export function clearRegionOpacity(region) {
  getRegionElements(region).forEach((el) => {
    el.classList.remove('glimm-content-fade');
    el.style.transition = '';
    el.style.opacity = '';
  });
}

export function getContentFadeTiming(aesthetics = {}) {
  const sweepMs = Number(aesthetics.sweepMs ?? 800);
  const midpoint = Number(aesthetics.midpoint ?? 0.5);
  const fadeOpacity = Number(aesthetics.contentFadeOpacity ?? 0.8);

  return {
    fadeOpacity,
    fadeOutMs: Math.round(sweepMs * midpoint),
    fadeInMs: Math.round(sweepMs * (1 - midpoint)),
  };
}

const HEADER_NAV_ROOT = 'header, .site-header';

let cachedGtagIds = null;

/** Discover gtag measurement / tag IDs already configured on the page. */
export function discoverGtagMeasurementIds() {
  const ids = new Set();

  document.querySelectorAll('script[src*="googletagmanager.com/gtag/js"]').forEach((script) => {
    try {
      const id = new URL(script.src, window.location.origin).searchParams.get('id');
      if (id) ids.add(id);
    } catch {
      /* ignore */
    }
  });

  document.querySelectorAll('script').forEach((script) => {
    const text = script.textContent || '';
    const configMatches = text.matchAll(/gtag\s*\(\s*['"]config['"]\s*,\s*['"]([^'"]+)['"]/g);
    for (const match of configMatches) {
      ids.add(match[1]);
    }
  });

  const configured = window.glimmTransitions?.analyticsIds;
  if (Array.isArray(configured)) {
    configured.forEach((id) => {
      if (id) ids.add(String(id));
    });
  }

  return [...ids];
}

/** Prefer Google Tag (GT-) IDs over GA4 property IDs to avoid duplicate pageviews. */
export function resolveAnalyticsIds(ids) {
  const tagIds = ids.filter((id) => id.startsWith('GT-'));
  if (tagIds.length) return tagIds;
  return ids.filter((id) => id.startsWith('G-'));
}

export function getGtagMeasurementIds() {
  if (!cachedGtagIds) {
    cachedGtagIds = resolveAnalyticsIds(discoverGtagMeasurementIds());
  }
  return cachedGtagIds;
}

export function invalidateGtagCache() {
  cachedGtagIds = null;
}

function isMobileNavViewport() {
  return window.matchMedia('(max-width: 599px)').matches;
}

/**
 * Close the mobile navigation overlay and reset modal scroll lock.
 * Uses the native close control when available so Interactivity API state stays in sync.
 */
export function closeMobileNavigation() {
  document.documentElement.classList.remove('has-modal-open');
  document.body.classList.remove('has-modal-open');
  document.documentElement.style.overflow = '';
  document.body.style.overflow = '';

  if (!isMobileNavViewport()) {
    return;
  }

  document.querySelectorAll('.wp-block-navigation').forEach((nav) => {
    const openContainer = nav.querySelector('.wp-block-navigation__responsive-container.is-menu-open');
    if (openContainer) {
      const closeBtn = openContainer.querySelector(
        '.wp-block-navigation__responsive-container-close, .wp-block-navigation-overlay-close'
      );
      closeBtn?.click();
    }

    nav.querySelectorAll('.wp-block-navigation__responsive-container.is-menu-open').forEach((container) => {
      container.classList.remove('is-menu-open');
      container.removeAttribute('aria-modal');
      container.setAttribute('aria-hidden', 'true');
    });

    const openBtn = nav.querySelector('.wp-block-navigation__responsive-container-open');
    openBtn?.setAttribute('aria-expanded', 'false');
  });
}

/**
 * Reset navigation interactivity context from the fetched page (closed menu state).
 */
export function syncNavigationInteractivityState(fromDoc) {
  const liveNav = document.querySelector('header .wp-block-navigation');
  const nextNav = fromDoc?.querySelector('header .wp-block-navigation');
  if (!liveNav || !nextNav) return;

  const nextContext = nextNav.getAttribute('data-wp-context');
  if (nextContext) {
    liveNav.setAttribute('data-wp-context', nextContext);
  }

  const liveContainer = liveNav.querySelector('.wp-block-navigation__responsive-container');
  const nextContainer = nextNav.querySelector('.wp-block-navigation__responsive-container');
  if (!liveContainer || !nextContainer) return;

  liveContainer.classList.remove('is-menu-open');
  liveContainer.removeAttribute('aria-modal');

  if (isMobileNavViewport()) {
    liveContainer.setAttribute('aria-hidden', 'true');
  } else {
    liveContainer.removeAttribute('aria-hidden');
  }

  const openBtn = liveNav.querySelector('.wp-block-navigation__responsive-container-open');
  openBtn?.setAttribute('aria-expanded', 'false');
}

/**
 * Update header nav active item (current-menu-item, aria-current) after PJAX.
 */
export function syncHeaderNavigation(href) {
  const activePath = normalizePath(new URL(normalizeHref(href)).pathname);
  const header = document.querySelector(HEADER_NAV_ROOT);
  if (!header) return;

  header.querySelectorAll('a[href]').forEach((anchor) => {
    if (!isInternalLink(anchor)) return;

    const item = anchor.closest('.wp-block-navigation-item, .menu-item, li');
    let linkPath;
    try {
      linkPath = normalizePath(new URL(normalizeHref(anchor.href)).pathname);
    } catch {
      return;
    }

    const isActive = linkPath === activePath;
    if (isActive) {
      anchor.setAttribute('aria-current', 'page');
      item?.classList.add('current-menu-item');
    } else {
      anchor.removeAttribute('aria-current');
      item?.classList.remove('current-menu-item');
    }
  });
}

/** Replace the WordPress admin bar from a fetched document when logged in. */
export function syncAdminBar(fromDoc) {
  const nextBar = fromDoc.getElementById('wpadminbar');
  const currentBar = document.getElementById('wpadminbar');
  if (!nextBar || !currentBar) return;

  currentBar.replaceWith(document.importNode(nextBar, true));
}

/** Fire GA4 / gtag virtual page views after PJAX navigation. */
export function notifyAnalyticsPageView(href, title = document.title) {
  const gtag = typeof window.gtag === 'function' ? window.gtag : null;
  if (!gtag) return;

  const url = new URL(normalizeHref(href));
  const pageLocation = url.href;
  const pagePath = url.pathname + url.search;
  const pageTitle = title || document.title;

  getGtagMeasurementIds().forEach((measurementId) => {
    gtag('config', measurementId, {
      page_title: pageTitle,
      page_location: pageLocation,
      page_path: pagePath,
    });
  });

  debugLog(window.glimmTransitions, 'analytics page_view', pagePath, pageTitle);
}

/**
 * Run post-navigation updates: nav active state, admin bar, analytics.
 */
export function runPostNavigationUpdates(fromDoc, href, title = document.title) {
  invalidateGtagCache();
  closeMobileNavigation();
  syncNavigationInteractivityState(fromDoc);
  syncHeaderNavigation(href);
  syncAdminBar(fromDoc);
  notifyAnalyticsPageView(href, title);
}
